import { useCallback, useRef, useState } from 'react';
import api from '../../services/api';
import { Goal, Task, LifeCategory } from './types';

export interface Project {
  id: number;
  userId: number;
  name: string;
  createdAt: string;
  updatedAt: string;
}

export const useGoalsData = () => {
  const [goals, setGoals] = useState<Goal[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [categories, setCategories] = useState<LifeCategory[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const requestVersion = useRef(0);
  const taskRevision = useRef(0);
  const taskChanges = useRef(new Map<number, Map<keyof Task, { revision: number; value: unknown }>>());
  const goalsRef = useRef(goals);
  goalsRef.current = goals;

  // A mutation response is authoritative; an earlier GET must not overwrite it.
  const applyTaskUpdates = useCallback((updates: Array<Partial<Task> & { id: number }>) => {
    const revision = ++taskRevision.current;
    updates.forEach(({ id, ...patch }) => {
      const fields = taskChanges.current.get(id) || new Map<keyof Task, { revision: number; value: unknown }>();
      Object.entries(patch).forEach(([key, value]) => { fields.set(key as keyof Task, { revision, value }); });
      taskChanges.current.set(id, fields);
    });
    const byId = new Map(updates.map(task => [task.id, task]));
    setTasks(current => current.map(task => ({ ...task, ...byId.get(task.id) })));
  }, []);

  const loadData = useCallback(async () => {
    const version = ++requestVersion.current;
    const revision = taskRevision.current;
    try {
      const results = await Promise.allSettled([
        api.get('/api/goals'),
        api.get('/api/tasks'),
        api.get('/api/life-categories'),
        api.get('/api/projects')
      ]);
      if (version !== requestVersion.current) return goalsRef.current;
      const setters = [setGoals, setTasks, setCategories, setProjects];
      results.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          if (index !== 1) {
            setters[index](result.value.data);
          } else {
            // Keep only fields saved since this GET began. Other cards (including
            // newly created ones) can still be refreshed from the same response.
            taskChanges.current.forEach((fields, id) => {
              fields.forEach((change, key) => { if (change.revision <= revision) fields.delete(key); });
              if (!fields.size) taskChanges.current.delete(id);
            });
            setTasks(current => {
              const incoming: Task[] = result.value.data;
              const incomingIds = new Set(incoming.map(task => task.id));
              const merged = incoming.map(task => {
                const patch: Partial<Task> = {};
                taskChanges.current.get(task.id)?.forEach((change, key) => {
                  if (change.revision > revision) (patch as Record<string, unknown>)[key] = change.value;
                });
                return { ...task, ...patch };
              });
              return merged.concat(current.filter(task => !incomingIds.has(task.id) &&
                Array.from(taskChanges.current.get(task.id)?.values() || []).some(change => change.revision > revision)));
            });
          }
        } else {
          console.error('Failed to load data:', result.reason);
        }
      });
      return results[0].status === 'fulfilled' ? results[0].value.data : goalsRef.current;
    } finally {
      if (version === requestVersion.current) setLoading(false);
    }
  }, []);

  return {
    goals,
    tasks,
    categories,
    projects,
    loading,
    loadData,
    applyTaskUpdates,
    setGoals,
    setTasks,
    setCategories,
    setProjects
  };
};
